/**
 * The save payload for the permissions matrix.
 *
 * A grant is just the roles ticked for a capability. The server re-derives
 * nothing from it and stores exactly these roles, so what the operator sees in
 * the row is what governs access.
 */

import type { RoleKey } from './types'

/** Scope of the product-wide grant Careforce maintains for every organization. */
export const PRODUCT_WIDE_SCOPE = '*'

export interface CapabilityGrant {
  roles: RoleKey[]
  product_roles?: RoleKey[]
  /**
   * Which organizations hold the capability: `['*']` for one row serving
   * everybody, otherwise a slug per row. Careforce only; a customer's matrix
   * neither receives nor sends it.
   */
  orgs?: string[]
  updated_at?: string
}

/**
 * Save payload: the explicit roles for each changed capability, plus the org
 * scope for each row whose organizations changed.
 *
 * A row may carry both. The server applies the scope change first and then the
 * roles, so editing who holds a capability and who inside it may use it in one
 * save does what the screen showed.
 */
export function buildGrants(
  selection: Record<string, RoleKey[]>,
  ids: string[],
  orgScopes: Record<string, string[]> = {}
): Record<string, CapabilityGrant> {
  const grants: Record<string, CapabilityGrant> = {}
  for (const id of ids) {
    grants[id] = { roles: selection[id] ?? [] }
  }
  for (const [id, orgs] of Object.entries(orgScopes)) {
    grants[id] = { ...(grants[id] ?? { roles: selection[id] ?? [] }), orgs }
  }
  return grants
}

export function saveConfirmCopy(
  scope: string,
  count: number
): { title: string; description: string } {
  const n = `${count} ${count === 1 ? 'change' : 'changes'}`
  if (scope === PRODUCT_WIDE_SCOPE) {
    return {
      title: `Apply ${n} to ALL organizations?`,
      description:
        'These are product-wide defaults, not settings for one customer.',
    }
  }
  return {
    title: `Apply ${n} to ${scope} only?`,
    description:
      'This organization decides who among its own roles may use these. Careforce decides which capabilities it has at all.',
  }
}
